// Which charged lines have a document behind them, and which months do not.
//
// Luyanda: "Please show me which months are missing Telematics, Insurance etc."
//
// TWO THINGS THIS SCREEN REFUSES TO DO.
//
// It does not call a charge missing when a document genuinely covers it. A
// Bulldog carwash month is closed by one invoice for the whole fleet, so those
// months read as accounted for with the invoice named on the row.
//
// It does not call a charge accounted for when nobody has the paper. Until
// 9 September 2026 the whole carwash line was suppressed here, on the reasoning
// that a rule means no document is expected. That was wrong twice over. Before
// Bulldog, carwashes were charged per wash and a receipt for every one of them
// is on the driver's shift submission, so 230 vehicle-months and R157,600 had
// evidence that had simply never been loaded. And from July 2026 there is no
// Bulldog invoice at all, so 51 vehicle-months and R58,650 are charged with
// nothing behind them. Both were invisible. See migration 0188.
//
// It does not hide a line that is fully backed. A line at 100 percent is the
// evidence that the check is running, and a screen that only ever shows problems
// cannot tell you the difference between "no problems" and "not looking".
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FileCheck2, ChevronRight, Upload, Paperclip, CircleAlert } from 'lucide-react'
import {
  useGetEvidenceCoverage,
  useGetCoverageGaps,
  useGetXeroSupport,
  useGetVehicleRecordShortage,
  useGetVehicleMissingDetail,
} from '../../hooks/backend/statements'
import { Button } from '../../lib/shadcn/button'
import { ScheduleUploadDialog } from './ScheduleUploadDialog'
import { DriveWatch } from './DriveWatch'
import { AttachDocumentDialog } from './AttachDocumentDialog'
import { Spinner } from '../../lib/shadcn/spinner'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { cn } from '../../lib/shadcn/utils'
import { formatZar, formatInt } from '../../utils/statements'
import {
  type EvidenceCoverage as Coverage,
  type EvidenceCoverageLine,
  EMPTY_COVERAGE,
  lineLabel,
  backingNote,
  monthLabel,
} from '../../utils/evidenceCoverage'

type XeroRow = {
  line_code: string
  period_start: string
  month: string
  xero_bills: number
  xero_total_zar: number | null
  xero_with_attachments: number
  supplier_names: string | null
  position: string
}

type ShortageRow = {
  vehicle_id: string
  plate: string
  owner_name: string | null
  charged_lines: number
  lines_with_document: number
  lines_without_document: number
  complete_pct: number
  charged_zar: number
  unbacked_zar: number
  lines_short: string | null
}

// One missing item behind a car's completion number: one line, one month, one
// car. Mirrors api/src/handlers/statements/getVehicleMissingDetail.ts.
type MissingItem = {
  vehicle_id: string
  plate: string
  period_start: string
  month: string
  line_code: string
  line_label: string
  amount_zar: number
  is_draft: boolean
  statement_id: string | null
  what_would_close_it: string
}

type GapRow = {
  month: string
  line_code: string
  vehicle_id: string
  plate: string
  owner_name: string | null
  statement_id: string | null
  is_draft: boolean
  amount_zar: number
  what_would_close_it: string
}

// Only two lines have a document a reader can parse into per-vehicle amounts.
// The others say what would close them and offer no button, because a button
// that leads nowhere is worse than a sentence that tells the truth: the
// licensing receipts in Xero carry no plate in any field, and no upload here can
// change that.
const HAS_A_READER = new Set(['insurance', 'telemetric_system'])

function pctTone(pct: number): string {
  if (pct >= 99.5) return 'text-emerald-700'
  if (pct >= 85) return 'text-amber-700'
  return 'text-red-700'
}

function Bar({ row }: { row: EvidenceCoverageLine }) {
  const total = Math.max(row.figures, 1)
  const doc = (row.with_document / total) * 100
  const rule = (row.rule_backed / total) * 100
  const miss = (row.missing / total) * 100
  return (
    <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted" aria-hidden="true">
      <div className="bg-emerald-600" style={{ width: `${doc}%` }} />
      <div className="bg-sky-600" style={{ width: `${rule}%` }} />
      <div className="bg-red-500" style={{ width: `${miss}%` }} />
    </div>
  )
}

// Each vehicle's record, read down the fleet instead of across the calendar.
//
// Luyanda, 9 September 2026: "a view on the 'what has no document' tab at the
// bottom that shows vehicles with their record completion shortage across all
// line items."
//
// Worst first, and worst means least complete rather than largest rand. A car
// at 0 percent with R10,822 behind it is a harder conversation than one at 90
// percent with R40,000: the first has nothing at all to show its owner.
function ShortageTable() {
  const q = useGetVehicleRecordShortage<ShortageRow[]>()
  const { trigger } = q
  const detail = useGetVehicleMissingDetail<MissingItem[]>()
  const detailTrigger = detail.trigger
  const [showAll, setShowAll] = useState(false)

  // One car open at a time, the same rule the month table follows. A screen
  // that expands every car at once is a screen nobody scrolls to the bottom of.
  const [openVehicle, setOpenVehicle] = useState<string | null>(null)
  const [items, setItems] = useState<MissingItem[] | null>(null)
  const [detailError, setDetailError] = useState<string | null>(null)
  // The car whose answer is wanted right now. A slow reply for a car that has
  // since been closed must not land under a different plate.
  const wanted = useRef<string | null>(null)

  const [uploadFor, setUploadFor] = useState<{ line_code: string; month: string } | null>(null)
  const [attachTo, setAttachTo] = useState<
    { vehicle_id: string; plate: string; line_code: string; month: string; charged: number } | null
  >(null)

  useEffect(() => {
    trigger({}, { skipCache: true })
  }, [trigger])

  const loadDetail = useCallback(
    async (vehicleId: string) => {
      wanted.current = vehicleId
      setItems(null)
      setDetailError(null)
      try {
        const rows = (await detailTrigger(
          { vehicle_id: vehicleId },
          { skipCache: true },
        )) as MissingItem[] | null
        if (wanted.current !== vehicleId) return
        setItems((rows ?? []).filter((r) => r.vehicle_id === vehicleId))
      } catch (err) {
        if (wanted.current !== vehicleId) return
        // The handler's or the database's own words. "The missing items could
        // not be loaded" tells the person nothing they can act on.
        setDetailError(err instanceof Error ? err.message : String(err))
      }
    },
    [detailTrigger],
  )

  const toggleVehicle = useCallback(
    (vehicleId: string) => {
      if (openVehicle === vehicleId) {
        wanted.current = null
        setOpenVehicle(null)
        setItems(null)
        setDetailError(null)
        return
      }
      setOpenVehicle(vehicleId)
      void loadDetail(vehicleId)
    },
    [loadDetail, openVehicle],
  )

  // After a document lands, both numbers move: the car's completion and the
  // list of items behind it. Reading one and not the other leaves the operator
  // looking at a row they have just closed.
  const afterDocument = useCallback(() => {
    void trigger({}, { skipCache: true })
    if (openVehicle) void loadDetail(openVehicle)
  }, [loadDetail, openVehicle, trigger])

  const rows = q.data ?? []
  const shown = showAll ? rows : rows.slice(0, 15)
  const short = rows.filter((r) => r.lines_without_document > 0).length

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="border-b border-border px-3 py-2">
        <div className="text-sm font-medium">Record completion, per vehicle</div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          The same shortage read down the fleet instead of across the calendar.
          {rows.length > 0 && (
            <>
              {' '}
              <span className="font-medium text-foreground">{formatInt(short)}</span> of{' '}
              {formatInt(rows.length)} vehicles have at least one charged line with no
              document behind it. Open a car's number to see exactly which items.
            </>
          )}
        </div>
      </div>

      {q.loading && rows.length === 0 && (
        <div className="flex items-center gap-2 px-3 py-6 text-xs text-muted-foreground">
          <Spinner className="h-3.5 w-3.5" /> Reading the fleet.
        </div>
      )}

      {q.error && (
        <div className="flex items-start gap-2 border-b border-border bg-red-50 px-3 py-2.5 text-xs text-red-800">
          <CircleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>The record completion list could not be read. {q.error}</span>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Vehicle</th>
              <th className="px-3 py-2 text-left font-medium">Owner</th>
              <th className="px-3 py-2 text-right font-medium">Complete</th>
              <th className="px-3 py-2 text-right font-medium">Lines short</th>
              <th className="px-3 py-2 text-right font-medium">Charged</th>
              <th className="px-3 py-2 text-right font-medium">Unbacked</th>
              <th className="px-3 py-2 text-left font-medium">What is short</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const open = openVehicle === r.vehicle_id
              return (
                <Fragment key={r.vehicle_id}>
                  <tr className={cn('border-t border-border', open && 'bg-accent/40')}>
                    <td className="px-3 py-2 font-medium">
                      <button
                        type="button"
                        onClick={() => toggleVehicle(r.vehicle_id)}
                        aria-expanded={open}
                        className="inline-flex items-center gap-1 text-left font-medium text-foreground"
                      >
                        <ChevronRight
                          className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-90')}
                        />
                        {r.plate}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{r.owner_name ?? '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => toggleVehicle(r.vehicle_id)}
                        aria-expanded={open}
                        title="Open the items behind this number"
                        className={cn(
                          'tabular-nums font-medium underline decoration-dotted underline-offset-2',
                          pctTone(r.complete_pct),
                        )}
                      >
                        {r.complete_pct.toFixed(0)}%
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => toggleVehicle(r.vehicle_id)}
                        aria-expanded={open}
                        title="Open the items behind this number"
                        className="tabular-nums underline decoration-dotted underline-offset-2"
                      >
                        {formatInt(r.lines_without_document)} of {formatInt(r.charged_lines)}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {formatZar(r.charged_zar)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.unbacked_zar > 0 ? formatZar(r.unbacked_zar) : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {r.lines_short
                        ? r.lines_short.split(', ').map((c) => lineLabel(c)).join(', ')
                        : 'Nothing. Every charged line has a document.'}
                    </td>
                  </tr>

                  {open && (
                    <tr className="border-t border-border">
                      <td colSpan={7} className="bg-muted/30 px-3 py-3">
                        {detailError ? (
                          <div className="flex items-start gap-2 rounded border border-red-200 bg-red-50 px-2.5 py-2 text-xs text-red-800">
                            <CircleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            <span>
                              The missing items for {r.plate} could not be read. {detailError}
                            </span>
                          </div>
                        ) : items === null ? (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Spinner className="h-3.5 w-3.5" /> Reading what is missing on{' '}
                            {r.plate}.
                          </div>
                        ) : items.length === 0 ? (
                          <p className="text-xs text-muted-foreground">
                            Nothing is missing on {r.plate}. Every line charged to this car has a
                            document or a dated rule behind it.
                          </p>
                        ) : (
                          <>
                            <p className="mb-2 text-[11px] text-muted-foreground">
                              {formatInt(items.length)} item{items.length === 1 ? '' : 's'} on{' '}
                              {r.plate} with nothing behind {items.length === 1 ? 'it' : 'them'}.
                              Each one takes its own document.
                            </p>
                            <div className="overflow-x-auto rounded border border-border bg-card">
                              <table className="w-full min-w-[640px] text-xs">
                                <thead>
                                  <tr className="border-b border-border text-left uppercase tracking-wide text-muted-foreground">
                                    <th className="px-2.5 py-1.5 font-medium">Month</th>
                                    <th className="px-2.5 py-1.5 font-medium">Line</th>
                                    <th className="px-2.5 py-1.5 text-right font-medium">Charged</th>
                                    <th className="px-2.5 py-1.5 font-medium">What is missing</th>
                                    <th className="px-2.5 py-1.5" />
                                  </tr>
                                </thead>
                                <tbody>
                                  {items.map((it) => (
                                    <tr
                                      key={`${it.line_code}:${it.month}`}
                                      className="border-b border-border/60 align-top last:border-0"
                                    >
                                      <td className="whitespace-nowrap px-2.5 py-1.5 font-medium">
                                        {monthLabel(it.month)}
                                        {it.is_draft && (
                                          <span className="ml-1.5 rounded bg-muted px-1 py-0.5 text-[10px] font-normal text-muted-foreground">
                                            draft
                                          </span>
                                        )}
                                      </td>
                                      <td className="whitespace-nowrap px-2.5 py-1.5">
                                        {lineLabel(it.line_code)}
                                      </td>
                                      <td className="px-2.5 py-1.5 text-right tabular-nums">
                                        {formatZar(it.amount_zar)}
                                      </td>
                                      <td className="max-w-[420px] px-2.5 py-1.5 leading-snug text-muted-foreground">
                                        {it.what_would_close_it}
                                      </td>
                                      <td className="whitespace-nowrap px-2.5 py-1.5 text-right">
                                        <div className="inline-flex items-center gap-1.5">
                                          {HAS_A_READER.has(it.line_code) && (
                                            <button
                                              type="button"
                                              onClick={() =>
                                                setUploadFor({
                                                  line_code: it.line_code,
                                                  month: it.month,
                                                })
                                              }
                                              title="The supplier's own schedule for this month. The reader proposes it and you confirm it, exactly as on the month above."
                                              className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[11px] text-foreground hover:bg-accent"
                                            >
                                              <Upload className="h-3 w-3" />
                                              Upload schedule
                                            </button>
                                          )}
                                          <button
                                            type="button"
                                            onClick={() =>
                                              setAttachTo({
                                                vehicle_id: it.vehicle_id,
                                                plate: it.plate,
                                                line_code: it.line_code,
                                                month: it.month,
                                                charged: it.amount_zar,
                                              })
                                            }
                                            title={`Attach one document to ${it.plate} for ${lineLabel(it.line_code)} in ${monthLabel(it.month)}`}
                                            className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[11px] text-foreground hover:bg-accent"
                                          >
                                            <Paperclip className="h-3 w-3" />
                                            Attach
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
            {rows.length === 0 && !q.loading && !q.error && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-xs text-muted-foreground">
                  Nothing charged to any vehicle yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {rows.length > 15 && (
        <div className="border-t border-border px-3 py-2">
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'Show the worst fifteen' : `Show all ${formatInt(rows.length)} vehicles`}
          </Button>
        </div>
      )}

      {/* Both dialogs are the ones the rest of this screen already uses. A
          document uploaded from a car's own list therefore becomes evidence by
          exactly the same path, confirm gate included, as one uploaded from the
          month above it. */}
      {attachTo && (
        <AttachDocumentDialog
          open
          onOpenChange={(v) => {
            if (!v) setAttachTo(null)
          }}
          vehicleId={attachTo.vehicle_id}
          plate={attachTo.plate}
          lineCode={attachTo.line_code}
          month={attachTo.month}
          chargedZar={attachTo.charged}
          onAttached={afterDocument}
        />
      )}

      {uploadFor && (
        <ScheduleUploadDialog
          open
          onOpenChange={(v) => {
            if (!v) setUploadFor(null)
          }}
          lineCode={uploadFor.line_code}
          month={uploadFor.month}
          onConfirmed={afterDocument}
        />
      )}
    </div>
  )
}

export function EvidenceCoverage() {
  const query = useGetEvidenceCoverage<Coverage>()
  const { trigger } = query
  const [line, setLine] = useState<string>('')
  // The month whose vehicles are open. One at a time: a screen that expands
  // every month at once is a screen nobody scrolls to the bottom of.
  const [openMonth, setOpenMonth] = useState<string | null>(null)
  const [uploadFor, setUploadFor] = useState<{ line_code: string; month: string } | null>(null)
  const [attachTo, setAttachTo] = useState<
    { vehicle_id: string; plate: string; line_code: string; month: string; charged: number } | null
  >(null)

  const gaps = useGetCoverageGaps<GapRow[]>()
  const xero = useGetXeroSupport<XeroRow[]>()
  const gapsTrigger = gaps.trigger

  const reload = useCallback(() => {
    trigger({}, { skipCache: true })
    if (line) gapsTrigger({ line_code: line }, { skipCache: true })
  }, [gapsTrigger, line, trigger])

  useEffect(() => {
    trigger({}, { skipCache: true })
  }, [trigger])

  // The vehicles behind the numbers, fetched per line rather than per month, so
  // opening a second month costs nothing.
  useEffect(() => {
    if (line) gapsTrigger({ line_code: line }, { skipCache: true })
  }, [gapsTrigger, line])

  useEffect(() => {
    setOpenMonth(null)
  }, [line])

  const data = query.data ?? EMPTY_COVERAGE
  const lines = data.lines ?? []
  const months = data.months ?? []

  // Default the month table to the worst line by rand, because that is the one
  // somebody is going to be asked about.
  useEffect(() => {
    if (!line && lines.length > 0) setLine(lines[0]?.line_code ?? '')
  }, [line, lines])

  const monthRows = useMemo(
    () => months.filter((m) => m.line_code === line),
    [months, line],
  )

  const gapRows = (gaps.data as GapRow[] | undefined) ?? []
  const xeroByKey = useMemo(() => {
    const m = new Map<string, XeroRow>()
    for (const r of (xero.data as XeroRow[] | undefined) ?? []) {
      m.set(`${r.line_code}:${r.period_start.slice(0, 7)}`, r)
    }
    return m
  }, [xero.data])
  const gapsByMonth = useMemo(() => {
    const map = new Map<string, GapRow[]>()
    for (const g of gapRows) {
      const list = map.get(g.month)
      if (list) list.push(g)
      else map.set(g.month, [g])
    }
    return map
  }, [gapRows])

  const totals = useMemo(() => {
    const figures = lines.reduce((a, r) => a + r.figures, 0)
    const missing = lines.reduce((a, r) => a + r.missing, 0)
    const zar = lines.reduce((a, r) => a + r.zar_missing, 0)
    return { figures, missing, zar }
  }, [lines])

  if (query.loading && lines.length === 0) {
    return (
      <div className="flex items-center justify-center p-10">
        <Spinner />
      </div>
    )
  }

  if (lines.length === 0) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        No charged lines to check. This screen reads the statements that have been
        issued, so it stays empty until a statement exists.
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="-mx-4 -mt-4 rounded-none border-x-0 border-t-0">
        <DriveWatch />
      </div>

      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex items-start gap-3">
          <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="text-xs leading-relaxed text-muted-foreground">
            Every figure charged to an owner, and whether a document or a dated rule
            stands behind it. A vehicle-month is one line on one car for one month.
            {' '}
            <span className="font-medium text-foreground">
              {formatInt(totals.missing)} of {formatInt(totals.figures)}
            </span>{' '}
            have neither, worth {formatZar(totals.zar)}.
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
              <th className="px-3 py-2 font-medium">Line</th>
              <th className="px-3 py-2 font-medium">Coverage</th>
              <th className="px-3 py-2 text-right font-medium">Figures</th>
              <th className="px-3 py-2 text-right font-medium">Document</th>
              <th className="px-3 py-2 text-right font-medium">Rule</th>
              <th className="px-3 py-2 text-right font-medium">Missing</th>
              <th className="px-3 py-2 text-right font-medium">R missing</th>
              <th className="px-3 py-2 text-right font-medium">Months clean</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((row) => {
              const note = backingNote(row)
              return (
                <tr
                  key={row.line_code}
                  className={cn(
                    'border-b border-border/60 last:border-0',
                    row.line_code === line && 'bg-accent/40',
                  )}
                >
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => setLine(row.line_code)}
                      className="text-left font-medium text-foreground hover:underline"
                    >
                      {lineLabel(row.line_code)}
                    </button>
                    {note && (
                      <div className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
                        {note}
                      </div>
                    )}
                  </td>
                  <td className="w-40 px-3 py-2">
                    <Bar row={row} />
                    <div className={cn('mt-1 text-[11px] font-semibold', pctTone(row.pct_accounted_for))}>
                      {row.pct_accounted_for.toFixed(1)}%
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatInt(row.figures)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatInt(row.with_document)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {row.rule_backed > 0 ? formatInt(row.rule_backed) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {row.missing > 0 ? (
                      <button
                        type="button"
                        onClick={() => setLine(row.line_code)}
                        className="font-semibold text-red-700 underline decoration-dotted underline-offset-2"
                        title="Open the vehicle-months behind this number"
                      >
                        {formatInt(row.missing)}
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {row.zar_missing > 0 ? formatZar(row.zar_missing) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                    {formatInt(row.months_clean)} of{' '}
                    {formatInt(row.months_clean + row.months_with_gaps)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="rounded-lg border border-border bg-card">
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2">
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
            By month
          </span>
          <NativeSelect
            value={line}
            onChange={(e) => setLine(e.target.value)}
            className="h-8 w-auto text-xs"
          >
            {lines.map((row) => (
              <option key={row.line_code} value={row.line_code}>
                {lineLabel(row.line_code)}
              </option>
            ))}
          </NativeSelect>
          <span className="ml-auto text-[11px] text-muted-foreground">
            {formatInt(monthRows.length)} months
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-medium">Month</th>
                <th className="px-3 py-2 text-right font-medium">Figures</th>
                <th className="px-3 py-2 text-right font-medium">Backed</th>
                <th className="px-3 py-2 text-right font-medium">Missing</th>
                <th className="px-3 py-2 text-right font-medium">R missing</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {monthRows.map((m) => {
                const open = openMonth === m.month
                const rows = gapsByMonth.get(m.month) ?? []
                return (
                  <Fragment key={`${m.line_code}:${m.month}`}>
                    <tr className={cn('border-b border-border/60', open && 'bg-accent/40')}>
                      <td className="px-3 py-2">
                        {m.missing > 0 ? (
                          <button
                            type="button"
                            onClick={() => setOpenMonth(open ? null : m.month)}
                            aria-expanded={open}
                            className="inline-flex items-center gap-1 font-medium text-foreground"
                          >
                            <ChevronRight
                              className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-90')}
                            />
                            {monthLabel(m.month)}
                          </button>
                        ) : (
                          <span className="pl-[18px]">{monthLabel(m.month)}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatInt(m.figures)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatInt(m.with_document + m.rule_backed)}
                      </td>
                      <td
                        className={cn(
                          'px-3 py-2 text-right tabular-nums',
                          m.missing > 0 ? 'font-semibold text-red-700' : 'text-muted-foreground',
                        )}
                      >
                        {m.missing > 0 ? formatInt(m.missing) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {m.zar_missing > 0 ? formatZar(m.zar_missing) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {m.missing > 0 && HAS_A_READER.has(m.line_code) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setUploadFor({ line_code: m.line_code, month: m.month })}
                          >
                            <Upload className="mr-1.5 h-3.5 w-3.5" />
                            Upload
                          </Button>
                        )}
                      </td>
                    </tr>
                    {open && (
                      <tr className="border-b border-border/60">
                        <td colSpan={6} className="bg-muted/30 px-3 py-3">
                          {gaps.loading && rows.length === 0 ? (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Spinner className="h-3.5 w-3.5" /> Loading the vehicles…
                            </div>
                          ) : rows.length === 0 ? (
                            <p className="text-xs text-muted-foreground">
                              Nothing to list for this month.
                            </p>
                          ) : (
                            <>
                              <p className="mb-2 max-w-3xl text-[11px] leading-relaxed text-muted-foreground">
                                {rows[0]?.what_would_close_it}
                              </p>
                              {(() => {
                                const x = xeroByKey.get(`${m.line_code}:${m.month}`)
                                if (!x) return null
                                return (
                                  <p
                                    className={cn(
                                      'mb-2 max-w-3xl rounded border px-2.5 py-1.5 text-[11px] leading-relaxed',
                                      x.xero_bills > 0
                                        ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                                        : 'border-border bg-muted/50 text-muted-foreground',
                                    )}
                                  >
                                    <span className="font-medium">Xero: </span>
                                    {x.position}
                                    {x.supplier_names ? ` ${x.supplier_names}.` : ''}
                                  </p>
                                )
                              })()}
                              <div className="overflow-x-auto rounded border border-border bg-card">
                                <table className="w-full min-w-[440px] text-xs">
                                  <thead>
                                    <tr className="border-b border-border text-left uppercase tracking-wide text-muted-foreground">
                                      <th className="px-2.5 py-1.5 font-medium">Vehicle</th>
                                      <th className="px-2.5 py-1.5 font-medium">Owner</th>
                                      <th className="px-2.5 py-1.5 text-right font-medium">Charged</th>
                                      <th className="px-2.5 py-1.5" />
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {rows.map((g) => (
                                      <tr key={g.vehicle_id} className="border-b border-border/60 last:border-0">
                                        <td className="whitespace-nowrap px-2.5 py-1.5 font-medium">
                                          {g.plate}
                                          {g.is_draft && (
                                            <span className="ml-1.5 rounded bg-muted px-1 py-0.5 text-[10px] font-normal text-muted-foreground">
                                              draft
                                            </span>
                                          )}
                                        </td>
                                        <td className="px-2.5 py-1.5 text-muted-foreground">
                                          {g.owner_name ?? '—'}
                                        </td>
                                        <td className="px-2.5 py-1.5 text-right tabular-nums">
                                          {formatZar(g.amount_zar)}
                                        </td>
                                        <td className="px-2.5 py-1.5 text-right">
                                          <button
                                            type="button"
                                            onClick={() =>
                                              setAttachTo({
                                                vehicle_id: g.vehicle_id,
                                                plate: g.plate,
                                                line_code: m.line_code,
                                                month: m.month,
                                                charged: g.amount_zar,
                                              })
                                            }
                                            className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[11px] text-foreground hover:bg-accent"
                                          >
                                            <Paperclip className="h-3 w-3" />
                                            Attach
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
              {monthRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-xs text-muted-foreground">
                    Nothing charged on this line yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ShortageTable />

      {attachTo && (
        <AttachDocumentDialog
          open
          onOpenChange={(v) => { if (!v) setAttachTo(null) }}
          vehicleId={attachTo.vehicle_id}
          plate={attachTo.plate}
          lineCode={attachTo.line_code}
          month={attachTo.month}
          chargedZar={attachTo.charged}
          onAttached={() => { void query.trigger({}, { skipCache: true }); void gaps.trigger({}, { skipCache: true }); void xero.trigger({}, { skipCache: true }) }}
        />
      )}

      {uploadFor && (
        <ScheduleUploadDialog
          open
          onOpenChange={(v) => {
            if (!v) setUploadFor(null)
          }}
          lineCode={uploadFor.line_code}
          month={uploadFor.month}
          onConfirmed={reload}
        />
      )}
    </div>
  )
}
